import { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { MoreVertical, Ban, ShieldCheck, Trash2, Coins, Trash, Eye, EyeOff } from "lucide-react";
import {
  adminAdjustBalance, adminListUsers, adminBanUser, adminUnbanUser, adminDeleteUser,
  adminWipeTestData,
} from "@/lib/afritask";

const fmtNaira = (n) => `₦${Number(n || 0).toLocaleString("en-NG")}`;

// Mask a phone like "+2348011234567" → "+234 80 **** 4567"
const maskPhone = (phone) => {
  if (!phone) return "—";
  const digits = phone.replace(/[^\d+]/g, "");
  if (digits.length < 8) return digits;
  // Assume +CCXXYYYYZZZZ — keep leading 3-4 chars + last 4, mask middle
  const cc = digits.startsWith("+") ? digits.slice(0, 4) : digits.slice(0, 3);
  const rest = digits.slice(cc.length);
  const head = rest.slice(0, 2);
  const tail = rest.slice(-4);
  return `${cc} ${head} **** ${tail}`;
};

const displayNameOf = (u) => u.display_name || u.full_name || u.username;
const isLegacyUser = (u) => !u.display_name && !u.full_name;

const TABS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "banned", label: "Banned" },
];

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [adjust, setAdjust] = useState(null);
  const [adjustWallet, setAdjustWallet] = useState("work_points");
  const [adjustDelta, setAdjustDelta] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [adjustHistory, setAdjustHistory] = useState([]);
  const [banTarget, setBanTarget] = useState(null);
  const [banReason, setBanReason] = useState("");
  const [delTarget, setDelTarget] = useState(null);
  const [delDryRun, setDelDryRun] = useState(null);
  const [delConfirmText, setDelConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipeDryRun, setWipeDryRun] = useState(null);
  const [revealed, setRevealed] = useState(() => new Set());

  const toggleReveal = (uid) =>
    setRevealed((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });

  const [country, setCountry] = useState(() => {
    try {
      const u = new URL(window.location.href);
      return u.searchParams.get("country") || "";
    } catch {
      return "";
    }
  });
  const [countryFacets, setCountryFacets] = useState([]);

  const load = () => {
    const params = country ? { country } : {};
    return adminListUsers(params)
      .then((r) => {
        setUsers(r.users || []);
        setCountryFacets(r.country_facets || []);
      })
      .catch(() => {
        setUsers([]);
        setCountryFacets([]);
      });
  };

  // Sync `country` state into the URL query string for shareable / refreshable state
  useEffect(() => {
    const u = new URL(window.location.href);
    if (country) u.searchParams.set("country", country);
    else u.searchParams.delete("country");
    window.history.replaceState({}, "", u.toString());
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  const filtered = useMemo(() => {
    let list = users;
    if (tab === "active") list = list.filter((u) => !u.is_banned);
    else if (tab === "banned") list = list.filter((u) => u.is_banned);
    const qTrim = q.trim();
    if (qTrim) {
      const needle = qTrim.toLowerCase();
      // Normalize phone-like queries: strip everything but digits + leading '+'
      const phoneNeedle = qTrim.replace(/[^\d+]/g, "");
      list = list.filter((u) => {
        const hay = [u.username, u.display_name, u.full_name, u.email]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (hay.includes(needle)) return true;
        if (phoneNeedle.length >= 3 && u.phone) {
          const uPhone = u.phone.replace(/[^\d+]/g, "");
          if (uPhone.includes(phoneNeedle.replace(/^\+/, ""))) return true;
          if (uPhone === phoneNeedle) return true;
        }
        return false;
      });
    }
    return list;
  }, [users, tab, q]);

  const openAdjust = async (u) => {
    setAdjust(u); setAdjustWallet("work_points"); setAdjustDelta(""); setAdjustReason("");
    setAdjustHistory([]);
    try {
      const { adminGetAdjustments } = await import("@/lib/afritask");
      const h = await adminGetAdjustments(u.user_id);
      setAdjustHistory(h || []);
    } catch { /* ignore */ }
  };

  const doAdjust = async () => {
    if (!adjustReason.trim() || adjustReason.trim().length < 3) {
      toast.error("Reason must be at least 3 characters"); return;
    }
    const d = Number(adjustDelta);
    if (!d || Number.isNaN(d)) { toast.error("Enter a non-zero delta"); return; }
    try {
      const r = await adminAdjustBalance(adjust.user_id, {
        wallet_type: adjustWallet, delta: d, reason: adjustReason.trim(),
      });
      const unit = adjustWallet === "work_points" ? "pts" : "₦";
      toast.success(`Adjusted ${adjust.username}: ${d > 0 ? "+" : ""}${d} ${unit} · New balance: ${unit}${r.new_balance.toLocaleString()}`);
      setAdjust(null); setAdjustDelta(""); setAdjustReason(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const doBan = async () => {
    setBusy(true);
    try {
      const r = await adminBanUser(banTarget.user_id, banReason);
      toast.success(`${banTarget.username} suspended · ${r.sessions_revoked} sessions revoked`);
      setBanTarget(null); setBanReason(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const doUnban = async (u) => {
    setBusy(true);
    try { await adminUnbanUser(u.user_id); toast.success("Un-suspended"); load(); }
    finally { setBusy(false); }
  };

  const startDelete = async (u) => {
    setDelTarget(u); setDelConfirmText(""); setDelDryRun(null);
    try {
      const r = await adminDeleteUser(u.user_id, false);
      setDelDryRun(r.would_delete);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dry-run failed");
      setDelTarget(null);
    }
  };
  const confirmDelete = async () => {
    if (delConfirmText !== delTarget.username) { toast.error("Type the username to confirm"); return; }
    setBusy(true);
    try {
      const r = await adminDeleteUser(delTarget.user_id, true);
      toast.success(`Deleted ${delTarget.username} · ${Object.values(r.deleted).reduce((a,b)=>a+(typeof b==='number'?b:0),0)} rows cleared`);
      setDelTarget(null); setDelDryRun(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };

  const startWipe = async () => {
    setWipeOpen(true); setWipeDryRun(null);
    try {
      const r = await adminWipeTestData(false);
      setWipeDryRun(r.would_delete);
    } catch (e) { toast.error(e?.response?.data?.detail || "Dry-run failed"); }
  };
  const confirmWipe = async () => {
    setBusy(true);
    try {
      const r = await adminWipeTestData(true);
      const total = Object.values(r.deleted || {}).reduce((a,b)=>a+(typeof b==='number'?b:0),0);
      toast.success(`Wiped ${total} rows across mocked/seed data`);
      setWipeOpen(false); setWipeDryRun(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Wipe failed"); }
    finally { setBusy(false); }
  };

  const activeCount = users.filter((u) => !u.is_banned).length;
  const bannedCount = users.filter((u) => u.is_banned).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Input placeholder="Search by name, handle, or phone…" value={q} onChange={(e) => setQ(e.target.value)}
                 className="max-w-sm" data-testid="user-search" />
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="rounded-md border border-neutral-200 bg-white px-2 py-1.5 text-sm text-neutral-800 focus:border-[#B45309] focus:outline-none focus:ring-1 focus:ring-[#B45309]/40"
            data-testid="admin-users-country-filter"
          >
            <option value="">All countries ({countryFacets.reduce((a, f) => a + f.count, 0)})</option>
            {countryFacets.map((f) => (
              <option key={f.country} value={f.country}>
                {f.country} — {f.count}
              </option>
            ))}
          </select>
        </div>
        <Button variant="outline" onClick={startWipe}
                className="border-red-200 text-red-600 hover:bg-red-50"
                data-testid="wipe-test-data-btn">
          <Trash className="mr-1.5 h-3.5 w-3.5" /> Wipe test data
        </Button>
      </div>

      <div className="flex gap-1 rounded-lg border border-neutral-200 bg-neutral-50 p-1" data-testid="user-tabs">
        {TABS.map((t) => {
          const count = t.key === "all" ? users.length : t.key === "active" ? activeCount : bannedCount;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
                    className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition ${
                      tab === t.key ? "bg-white text-neutral-900 shadow-sm" : "text-neutral-500 hover:text-neutral-800"
                    }`}
                    data-testid={`user-tab-${t.key}`}>
              {t.label} <span className="ml-1 text-xs text-neutral-400">({count})</span>
            </button>
          );
        })}
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Real name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Country</TableHead>
                <TableHead className="text-right">Points</TableHead>
                <TableHead className="text-right">Earnings</TableHead>
                <TableHead className="text-right">Entries</TableHead>
                <TableHead className="text-right">Wins</TableHead>
                <TableHead>Flags</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.slice(0, 200).map((u) => {
                const legacy = isLegacyUser(u);
                const showPhone = revealed.has(u.user_id);
                return (
                <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                  <TableCell className="min-w-[180px]">
                    <div className="flex flex-col leading-tight">
                      <span
                        className="font-medium text-neutral-900"
                        data-testid={`user-real-name-${u.user_id}`}
                      >
                        {displayNameOf(u)}
                      </span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-1">
                        <span
                          className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-[10px] text-neutral-500"
                          data-testid={`user-handle-${u.user_id}`}
                        >
                          {u.username}
                        </span>
                        {legacy && (
                          <span
                            className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
                            data-testid={`user-legacy-${u.user_id}`}
                            title="Legacy anon user — no real name captured at signup"
                          >
                            legacy
                          </span>
                        )}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {u.phone ? (
                      <div className="flex items-center gap-1.5">
                        <span
                          className="font-mono text-xs tabular-nums text-neutral-700"
                          data-testid={`user-phone-${u.user_id}`}
                        >
                          {showPhone ? u.phone : maskPhone(u.phone)}
                        </span>
                        <button
                          type="button"
                          onClick={() => toggleReveal(u.user_id)}
                          className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
                          data-testid={`reveal-phone-${u.user_id}`}
                          title={showPhone ? "Hide phone" : "Reveal phone"}
                        >
                          {showPhone ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-neutral-400">—</span>
                    )}
                  </TableCell>
                  <TableCell data-testid={`user-country-${u.user_id}`}>
                    {u.country ? (
                      <span className="inline-flex items-center rounded-md bg-[#FDF3E4] px-1.5 py-0.5 font-mono text-[11px] font-semibold text-[#B45309] ring-1 ring-[#B45309]/15">
                        {u.country}
                      </span>
                    ) : (
                      <span className="text-xs text-neutral-400">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{u.work_points.toLocaleString()}</TableCell>
                  <TableCell className="text-right tabular-nums text-emerald-700">{fmtNaira(u.earnings_ngn)}</TableCell>
                  <TableCell className="text-right tabular-nums">{u.entries_count}</TableCell>
                  <TableCell className="text-right tabular-nums">{u.wins_count}</TableCell>
                  <TableCell className="space-x-1">
                    {u.is_seed && <Badge variant="outline" className="text-[10px]">seed</Badge>}
                    {u.is_banned && (
                      <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-[10px]" data-testid={`banned-chip-${u.user_id}`}>
                        BANNED
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="rounded p-1 hover:bg-neutral-100"
                                data-testid={`user-menu-${u.user_id}`}>
                          <MoreVertical className="h-4 w-4 text-neutral-500" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openAdjust(u)} data-testid={`adjust-${u.user_id}`}>
                          <Coins className="mr-2 h-3.5 w-3.5" /> Adjust balance
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {u.is_banned ? (
                          <DropdownMenuItem onClick={() => doUnban(u)} data-testid={`unban-${u.user_id}`}>
                            <ShieldCheck className="mr-2 h-3.5 w-3.5" /> Unban
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => setBanTarget(u)} className="text-amber-700"
                                            data-testid={`ban-${u.user_id}`}>
                            <Ban className="mr-2 h-3.5 w-3.5" /> Ban
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => startDelete(u)} className="text-red-600"
                                          data-testid={`delete-${u.user_id}`}>
                          <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete account
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Adjust balance dialog — supports both wallets */}
      <Dialog open={!!adjust} onOpenChange={(o) => !o && setAdjust(null)}>
        <DialogContent className="max-w-lg" data-testid="adjust-dialog">
          <DialogHeader><DialogTitle>Adjust balance — {adjust?.username}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-neutral-700">Wallet</label>
              <select value={adjustWallet}
                      onChange={(e) => setAdjustWallet(e.target.value)}
                      className="mt-1 w-full rounded-md border border-neutral-200 px-3 py-2 text-sm"
                      data-testid="adjust-wallet-select">
                <option value="work_points">Work Points (pts)</option>
                <option value="earnings_ngn">Earnings Wallet (₦)</option>
              </select>
            </div>
            <div className="rounded-lg bg-neutral-50 p-3 text-xs">
              <p className="text-neutral-500">Current balance</p>
              <p className="mt-0.5 text-lg font-bold tabular-nums">
                {adjustWallet === "work_points"
                  ? `${(adjust?.work_points || 0).toLocaleString()} pts`
                  : `₦${(adjust?.earnings_ngn || 0).toLocaleString()}`}
              </p>
              <p className="mt-2 text-neutral-500">Delta (signed: use "-" to subtract)</p>
              <Input type="number" placeholder="+100 or -50"
                     value={adjustDelta}
                     onChange={(e) => setAdjustDelta(e.target.value)}
                     className="mt-1"
                     data-testid="adjust-delta-input" />
              {adjustDelta !== "" && !Number.isNaN(Number(adjustDelta)) && (
                <p className="mt-2 text-neutral-500">Preview new balance:{" "}
                  <span className={`font-bold tabular-nums ${
                    Number(adjustDelta) > 0 ? "text-emerald-700" : "text-red-600"
                  }`}>
                    {adjustWallet === "work_points"
                      ? `${((adjust?.work_points || 0) + Number(adjustDelta)).toLocaleString()} pts`
                      : `₦${((adjust?.earnings_ngn || 0) + Number(adjustDelta)).toLocaleString()}`}
                  </span>
                </p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-700">Reason (audit trail)</label>
              <Textarea rows={2} value={adjustReason}
                        onChange={(e) => setAdjustReason(e.target.value)}
                        placeholder="e.g. Compensation for withdrawal delay, or Corrective adjustment for double-credit"
                        className="mt-1"
                        data-testid="adjust-reason-input" />
            </div>
            {adjustHistory.length > 0 && (
              <details className="rounded-lg border border-neutral-200 bg-white p-2 text-xs">
                <summary className="cursor-pointer font-medium text-neutral-700">
                  Admin adjustments history ({adjustHistory.length})
                </summary>
                <ul className="mt-2 space-y-1.5" data-testid="adjust-history">
                  {adjustHistory.slice(0, 20).map((a, i) => (
                    <li key={i} className="border-l-2 border-neutral-200 pl-2">
                      <p className="text-[10px] text-neutral-500">
                        {new Date(a.timestamp).toLocaleString()} · {a.wallet_type}
                      </p>
                      <p><span className={`font-semibold ${a.delta > 0 ? "text-emerald-700" : "text-red-600"}`}>
                        {a.delta > 0 ? "+" : ""}{a.delta} {a.unit}
                      </span> · {(a.reason || "").replace(/^admin_adjustment:/, "")}</p>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdjust(null)}>Cancel</Button>
            <Button onClick={doAdjust} className="bg-amber-700 hover:bg-amber-800"
                    data-testid="adjust-save">
              Apply adjustment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ban dialog */}
      <Dialog open={!!banTarget} onOpenChange={(o) => !o && setBanTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspend {banTarget?.username}?</DialogTitle>
            <DialogDescription>
              Their active sessions will be revoked and they will be unable to log in.
              They will keep their wallet and history — this can be reversed by clicking Unban.
            </DialogDescription>
          </DialogHeader>
          <Textarea rows={3} value={banReason} onChange={(e) => setBanReason(e.target.value)}
                    placeholder="Reason for suspension (visible to admins)"
                    data-testid="ban-reason" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBanTarget(null)}>Cancel</Button>
            <Button onClick={doBan} disabled={busy}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="ban-confirm">
              Suspend account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog — 2 step confirmation */}
      <Dialog open={!!delTarget} onOpenChange={(o) => !o && (setDelTarget(null), setDelDryRun(null))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">
              <Trash2 className="mr-1 inline h-4 w-4" /> Delete {delTarget?.username}?
            </DialogTitle>
            <DialogDescription>
              This is IRREVERSIBLE. All the user&apos;s data will be permanently removed.
            </DialogDescription>
          </DialogHeader>
          {delDryRun ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs">
                <p className="mb-1 font-semibold text-red-700">This will delete:</p>
                <ul className="ml-4 list-disc space-y-0.5 text-red-800">
                  <li>1 user account ({delTarget?.username})</li>
                  <li>Wallet: {delDryRun.wallet_points?.toLocaleString?.() || 0} pts · {fmtNaira(delDryRun.earnings_ngn)}</li>
                  <li>{delDryRun.points_ledger_entries} points-ledger entries</li>
                  <li>{delDryRun.earnings_ledger_entries} earnings-ledger entries</li>
                  <li>{delDryRun.transactions} transactions</li>
                  <li>{delDryRun.task_entries} task entries</li>
                  <li>{delDryRun.notifications} notifications</li>
                  <li>{delDryRun.pending_withdrawals} pending withdrawals will be cancelled</li>
                  <li>{delDryRun.referrals_as_referrer} referrals as referrer will be deleted</li>
                </ul>
              </div>
              <div>
                <p className="mb-1 text-xs text-neutral-600">
                  Type <span className="font-mono font-bold">{delTarget?.username}</span> to confirm:
                </p>
                <Input value={delConfirmText} onChange={(e) => setDelConfirmText(e.target.value)}
                       placeholder={delTarget?.username}
                       data-testid="delete-confirm-input" />
              </div>
            </div>
          ) : (
            <p className="text-sm text-neutral-500">Loading dry-run…</p>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setDelTarget(null); setDelDryRun(null); }}>
              Cancel
            </Button>
            <Button onClick={confirmDelete}
                    disabled={busy || delConfirmText !== delTarget?.username}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="delete-confirm-btn">
              <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Permanently delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wipe test data dialog */}
      <Dialog open={wipeOpen} onOpenChange={(o) => !o && (setWipeOpen(false), setWipeDryRun(null))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Wipe test / seed data</DialogTitle>
            <DialogDescription>
              Delete all seeded showcase users + seeded tasks. Preserves the demo user (+2348103366815),
              AFRITASK system user, and any user with real activity.
            </DialogDescription>
          </DialogHeader>
          {wipeDryRun ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs">
              <p className="mb-1 font-semibold text-red-700">Will delete:</p>
              <ul className="ml-4 list-disc space-y-0.5 text-red-800">
                <li>{wipeDryRun.users_to_delete} users {wipeDryRun.usernames_sample?.length > 0 && `(e.g. ${wipeDryRun.usernames_sample.join(", ")})`}</li>
                <li>{wipeDryRun.tasks_to_delete} seeded tasks</li>
                <li>{wipeDryRun.task_entries_to_delete} task entries</li>
                <li>{wipeDryRun.notifications_to_delete} notifications</li>
                <li>{wipeDryRun.referrals_to_delete} referrals</li>
              </ul>
            </div>
          ) : (
            <p className="text-sm text-neutral-500">Loading dry-run…</p>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setWipeOpen(false); setWipeDryRun(null); }}>Cancel</Button>
            <Button onClick={confirmWipe} disabled={busy}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="wipe-confirm-btn">
              <Trash className="mr-1.5 h-3.5 w-3.5" /> Wipe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
